import type {
  KioskMediaAccess,
  LocationRichMediaResponse,
  RichMediaCategory,
  RichMediaFile,
  RichMediaItem,
} from '../../services/richMediaApi';

export type { KioskMediaAccess, LocationRichMediaResponse, RichMediaCategory, RichMediaFile, RichMediaItem };

export const OPEN_LOCATION = 'mediaManagement/OPEN_LOCATION';

export const LOAD_MEDIA_REQUEST = 'mediaManagement/LOAD_MEDIA_REQUEST';
export const LOAD_MEDIA_SUCCESS = 'mediaManagement/LOAD_MEDIA_SUCCESS';
export const LOAD_MEDIA_FAILURE = 'mediaManagement/LOAD_MEDIA_FAILURE';

export const LOAD_CATEGORIES_REQUEST = 'mediaManagement/LOAD_CATEGORIES_REQUEST';
export const LOAD_CATEGORIES_SUCCESS = 'mediaManagement/LOAD_CATEGORIES_SUCCESS';
export const LOAD_CATEGORIES_FAILURE = 'mediaManagement/LOAD_CATEGORIES_FAILURE';

export const SAVE_MEDIA_REQUEST = 'mediaManagement/SAVE_MEDIA_REQUEST';
export const SAVE_MEDIA_DONE = 'mediaManagement/SAVE_MEDIA_DONE';
export const SET_SLOT_PROGRESS = 'mediaManagement/SET_SLOT_PROGRESS';

export const SYNC_MEDIA_REQUEST = 'mediaManagement/SYNC_MEDIA_REQUEST';
export const SYNC_MEDIA_SUCCESS = 'mediaManagement/SYNC_MEDIA_SUCCESS';
export const SYNC_MEDIA_FAILURE = 'mediaManagement/SYNC_MEDIA_FAILURE';

export const LOAD_ACCESS_REQUEST = 'mediaManagement/LOAD_ACCESS_REQUEST';
export const LOAD_ACCESS_DONE = 'mediaManagement/LOAD_ACCESS_DONE';

export const ADD_TOAST = 'mediaManagement/ADD_TOAST';
export const DISMISS_TOAST = 'mediaManagement/DISMISS_TOAST';

/** Owner of a slot: the outlet (`location`) or an item id. */
export const LOCATION_OWNER = 'location';

/** Slot key used for progress and errors: `${owner}|${div}`. */
export const slotKey = (owner: string, div: string) => `${owner}|${div}`;

/** A saved file moved to another div by drag and drop; sent in one arrange call (P5) before the uploads. */
export interface SavePlacement {
  mediaId: string;
  div: string;
}

/** One upload (P2) or removal (P3), into the slot it is shown in. */
export interface SaveChange {
  key: string;
  kind: 'upload' | 'replace' | 'remove';
  div: string;
  /** Slot name for error toasts, e.g. "Thumbnail". */
  label: string;
  file?: File;
  /** Removal: the saved file shown in the slot. */
  mediaId?: string;
}

export interface SaveMediaPayload {
  requestId: number;
  locationId: string;
  owner: string;
  /** Moved saved files with the div each goes to; empty when nothing moved. */
  placements: SavePlacement[];
  changes: SaveChange[];
}

export interface SaveMediaResult {
  requestId: number;
  owner: string;
  /** Keys of the changes that were saved. */
  doneKeys: string[];
  /** Keys of the changes that failed, with the message to show under the slot. */
  failed: Record<string, string>;
  ok: boolean;
}

export type MediaToastType = 'success' | 'error' | 'info';

export interface MediaToast {
  id: number;
  message: string;
  type: MediaToastType;
  /** 'corner' = top-right card, 'bottom' = bottom-center pill. */
  placement: 'corner' | 'bottom';
}

/** Kiosk Media permissions of the signed-in employee; 'idle' until first read. */
export interface MediaAccessState extends KioskMediaAccess {
  status: 'idle' | 'loading' | 'ready';
}

export interface MediaManagementState {
  locationId: string | null;
  data: LocationRichMediaResponse | null;
  loading: boolean;
  loadError: string | null;
  categories: RichMediaCategory[];
  categoriesLocationId: string | null;
  categoriesError: string | null;
  /** Owner whose save is running. */
  saving: string | null;
  /** slot key -> upload progress (0–100) while its call runs. */
  progress: Record<string, number>;
  lastSave: SaveMediaResult | null;
  syncBusy: boolean;
  toasts: MediaToast[];
  access: MediaAccessState;
}
